import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { OrbiController } from '../orbi-lab/vendor/core/controller.js';
import { createMiniOrbiSwarm } from '../orbi-lab/vendor/core/mini-orbi-swarm.js';
import { createOrbi } from '../orbi-lab/vendor/core/orbi.js';
import * as miniOrbiLoader from '../orbi-lab/vendor/sequences/mini-orbi-loader-large/sequence.js';

/** Logische Bühne in Lab-Pixeln: die drei Minis (2, 4, 6) jonglieren um den
 *  Schwarm-Ursprung zwischen x −64…64 (± halbe Größe, Stauchung, Neigung) und
 *  y −38 (Scheitel des hohen Bogens) bis +105 (Bodenschatten). Die Bühne wird
 *  als Ganzes auf `[size]` skaliert, die Pixelwerte der Sequenz bleiben. */
const STAGE_WIDTH = 200;
const STAGE_HEIGHT = 146;

let instanceCounter = 0;

/**
 * Der Ladezustand des Portals (Ladezustände-Regel, CLAUDE.md Regel 19 /
 * design.md §5 „Ladezustände"): drei Mini-Orbis jonglieren phasenversetzt
 * (Lab-Sequenz `mini-orbi-loader-large`) — für jeden Bereich, der lädt oder
 * auf eine längere Arbeit wartet (Seite, Karte, Dialogkörper, Suchergebnis,
 * KI-Generierung). Knöpfe behalten `[nbSpinner]`.
 *
 * Gleiche Komponente wie `app-orbi-loader` im chat-widget; beide Motoren sind
 * Kopien von tools/orbi-animation-lab (hier `orbi-lab/vendor`).
 * Ohne View-Encapsulation, weil das Lab sein DOM selbst baut — alle eigenen
 * Selektoren hängen an `.al-orbi-loader`.
 *
 * Die Sequenz braucht einen Haupt-Orbi und den vollen Siebener-Pool; beides
 * wird gebaut, sichtbar sind nur die drei jonglierenden Minis. Beendet wird
 * der Loader durch Entfernen (`*ngIf`/`@if` → `ngOnDestroy`), nicht über das
 * Lab-Signal `mini-orbi-loader-complete`.
 */
@Component({
  selector: 'app-orbi-loader',
  template: `
    <div class="al-orbi-loader__box"
         [style.width.px]="size"
         [style.height.px]="size * stageHeight / stageWidth">
      <div class="al-orbi-loader__stage stage" #stage aria-hidden="true"
           [style.transform]="'scale(' + size / stageWidth + ')'">
        <div class="al-orbi-loader__main" #main></div>
      </div>
      @if (fallback) {
        <div class="al-orbi-loader__fallback" [nbSpinner]="true" nbSpinnerStatus="primary" nbSpinnerSize="large"></div>
      }
    </div>
    @if (label) {
      <p class="al-orbi-loader__label">{{ label }}</p>
    } @else {
      <span class="al-orbi-loader__sr">{{ 'components.orbiLoader.default' | translate }}</span>
    }
  `,
  styleUrls: ['./orbi-loader.component.scss', '../orbi-lab/vendor/core/orbi.css'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'al-orbi-loader',
    role: 'status',
    'aria-live': 'polite',
  },
})
export class OrbiLoaderComponent implements OnInit, OnDestroy {
  /** Gerenderte Breite der Orbi-Bühne in px; die Höhe folgt dem Seitenverhältnis. */
  @Input() size = 120;

  /** Was gerade passiert, schon übersetzt (`'…' | translate`). Pflicht bei
   *  langen Arbeiten (KI-Generierung, Sync, Import, Remote-Suche). */
  @Input() label: string | null = null;

  @ViewChild('stage', { static: true }) private stageRef!: ElementRef<HTMLDivElement>;
  @ViewChild('main', { static: true }) private mainRef!: ElementRef<HTMLDivElement>;

  readonly stageWidth = STAGE_WIDTH;
  readonly stageHeight = STAGE_HEIGHT;
  fallback = false;

  private controller: OrbiController | null = null;

  constructor(private readonly zone: NgZone) {}

  ngOnInit(): void {
    const id = `al-orbi-loader-${++instanceCounter}`;
    let orbi: ReturnType<typeof createOrbi>;
    let swarm: ReturnType<typeof createMiniOrbiSwarm>;
    try {
      orbi = createOrbi({ type: 'tutor', id });
      swarm = createMiniOrbiSwarm({ type: 'tutor', id: `${id}-swarm` });
    } catch {
      this.fallback = true;
      return;
    }
    orbi.root.removeAttribute('role');
    orbi.root.removeAttribute('aria-label');
    this.mainRef.nativeElement.appendChild(orbi.root);
    this.stageRef.nativeElement.appendChild(swarm.root);

    const registry = new Map<string, unknown>([[miniOrbiLoader.meta.id, miniOrbiLoader]]);
    this.controller = new OrbiController(orbi, registry);

    // Außerhalb der Zone: jeder Zyklus (2,2s) endet mit `finished`-Promises,
    // die sonst eine Change Detection im ganzen Portal auslösen.
    this.zone.runOutsideAngular(() => {
      this.controller?.play(miniOrbiLoader.meta.id).catch(() => {});
    });
  }

  ngOnDestroy(): void {
    this.controller?.stop();
  }
}
